import { useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
  FileText,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ChevronRight,
  Wallet,
  Building2,
  Users,
} from "lucide-react";
import { formatCurrency, formatDate, SourceBadge } from "./accountingUtils";

const StatCard = ({ icon: Icon, color, label, value, valueClass = "" }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${color} rounded-lg`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={`text-xl font-bold ${valueClass}`}>{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatementCard = ({ icon: Icon, title, subtitle, onClick, testId }) => (
  <Card className="cursor-pointer hover:shadow-md" onClick={onClick} data-testid={testId}>
    <CardContent className="pt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className="w-8 h-8 text-primary" />
          <div>
            <h3 className="font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </div>
    </CardContent>
  </Card>
);

/**
 * Combined "Overview" + "Statements" tab.
 * Pass `mode="overview"` to show the high-level summary (KPIs + clickable statement cards + recent entries).
 * Pass `mode="statements"` to show the detail breakdown grid.
 */
const FinancialStatements = ({ statements, journalEntries = [], mode = "overview" }) => {
  const [selectedStatement, setSelectedStatement] = useState(null);

  if (mode === "statements") {
    return (
      <div className="grid md:grid-cols-2 gap-6" data-testid="financial-statements-detail">
        {/* Trial Balance */}
        <Card>
          <CardHeader>
            <CardTitle>Trial Balance</CardTitle>
            <p className="text-sm text-muted-foreground">As of {statements.trialBalance?.as_of_date}</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-64">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-1">Account</th>
                    <th className="text-right p-1">Debit</th>
                    <th className="text-right p-1">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.trialBalance?.accounts?.map((acc, i) => (
                    <tr key={i} className="border-b last:border-0 text-xs">
                      <td className="p-1">{acc.account_name}</td>
                      <td className="p-1 text-right">{acc.debit > 0 ? formatCurrency(acc.debit) : "-"}</td>
                      <td className="p-1 text-right">{acc.credit > 0 ? formatCurrency(acc.credit) : "-"}</td>
                    </tr>
                  ))}
                  <tr className="font-bold border-t-2">
                    <td className="p-1">Total</td>
                    <td className="p-1 text-right">{formatCurrency(statements.trialBalance?.total_debit)}</td>
                    <td className="p-1 text-right">{formatCurrency(statements.trialBalance?.total_credit)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Income Statement Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Income Statement Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between p-2 bg-green-50 rounded">
              <span>Total Revenue</span>
              <span className="font-bold text-green-600">{formatCurrency(statements.incomeStatement?.revenue?.total)}</span>
            </div>
            <div className="flex justify-between p-2 bg-red-50 rounded">
              <span>Total Expenses</span>
              <span className="font-bold text-red-600">{formatCurrency(statements.incomeStatement?.expenses?.total)}</span>
            </div>
            <div className="flex justify-between p-3 bg-blue-50 rounded border-2 border-blue-200">
              <span className="font-semibold">{statements.incomeStatement?.net_income_label}</span>
              <span className={`font-bold text-lg ${(statements.incomeStatement?.net_income || 0) >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(statements.incomeStatement?.net_income)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Balance Sheet Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Balance Sheet Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between p-2 bg-blue-50 rounded">
              <span>Total Assets</span>
              <span className="font-bold">{formatCurrency(statements.balanceSheet?.assets?.total)}</span>
            </div>
            <div className="flex justify-between p-2 bg-yellow-50 rounded">
              <span>Total Liabilities</span>
              <span className="font-bold">{formatCurrency(statements.balanceSheet?.liabilities?.total)}</span>
            </div>
            <div className="flex justify-between p-2 bg-green-50 rounded">
              <span>Total Equity</span>
              <span className="font-bold">{formatCurrency(statements.balanceSheet?.equity?.total)}</span>
            </div>
            <div className="text-center text-sm text-muted-foreground">
              {statements.balanceSheet?.is_balanced ? "✓ Balanced" : "⚠ Not Balanced"}
            </div>
          </CardContent>
        </Card>

        {/* Cash Position */}
        <Card>
          <CardHeader>
            <CardTitle>Cash Position</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {statements.cashFlow?.cash_accounts?.map((acc, i) => (
                <div key={i} className="flex justify-between p-2 bg-gray-50 rounded">
                  <span>{acc.name}</span>
                  <span className="font-bold">{formatCurrency(acc.balance)}</span>
                </div>
              ))}
              <div className="flex justify-between p-3 bg-blue-50 rounded border-2 border-blue-200">
                <span className="font-semibold">Total Cash</span>
                <span className="font-bold text-lg">{formatCurrency(statements.cashFlow?.ending_cash)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // mode === "overview"
  return (
    <div className="space-y-6" data-testid="financial-statements-overview">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={TrendingUp}
          color="bg-green-100 text-green-600"
          label="Revenue"
          value={formatCurrency(statements.incomeStatement?.revenue?.total)}
        />
        <StatCard
          icon={TrendingDown}
          color="bg-red-100 text-red-600"
          label="Expenses"
          value={formatCurrency(statements.incomeStatement?.expenses?.total)}
        />
        <StatCard
          icon={DollarSign}
          color="bg-blue-100 text-blue-600"
          label="Net Income"
          value={formatCurrency(statements.incomeStatement?.net_income)}
          valueClass={(statements.incomeStatement?.net_income || 0) >= 0 ? "text-green-600" : "text-red-600"}
        />
        <StatCard
          icon={Wallet}
          color="bg-purple-100 text-purple-600"
          label="Total Assets"
          value={formatCurrency(statements.balanceSheet?.assets?.total)}
        />
      </div>

      {/* Financial Statements Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <StatementCard icon={FileText} title="Income Statement" subtitle="Profit & Loss Report" onClick={() => setSelectedStatement("income")} testId="open-income-statement" />
        <StatementCard icon={Building2} title="Balance Sheet" subtitle="Assets, Liabilities & Equity" onClick={() => setSelectedStatement("balance")} testId="open-balance-sheet" />
        <StatementCard icon={Wallet} title="Cash Flow Statement" subtitle="Cash Movement Report" onClick={() => setSelectedStatement("cashflow")} testId="open-cashflow-statement" />
        <StatementCard icon={Users} title="Statement of Equity" subtitle="Changes in Owner's Equity" onClick={() => setSelectedStatement("equity")} testId="open-equity-statement" />
      </div>

      {/* Recent Entries */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Journal Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {journalEntries.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No journal entries yet</p>
          ) : (
            <div className="space-y-2">
              {journalEntries.slice(0, 5).map((entry) => (
                <div key={entry.entry_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{entry.entry_number}</p>
                      <SourceBadge entry={entry} />
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{entry.description}</p>
                  </div>
                  <div className="text-right ml-3">
                    <p className="font-medium">{formatCurrency(entry.total_debit)}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(entry.entry_date)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statement Detail Modal */}
      {selectedStatement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                {selectedStatement === "income" && "Income Statement (Profit & Loss)"}
                {selectedStatement === "balance" && "Balance Sheet"}
                {selectedStatement === "cashflow" && "Cash Flow Statement"}
                {selectedStatement === "equity" && "Statement of Changes in Equity"}
              </CardTitle>
              <Button variant="ghost" onClick={() => setSelectedStatement(null)}>×</Button>
            </CardHeader>
            <CardContent>
              {selectedStatement === "income" && statements.incomeStatement && (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-green-600 mb-2">Revenue</h3>
                    {statements.incomeStatement.revenue.accounts.map((acc, i) => (
                      <div key={i} className="flex justify-between py-1 px-2">
                        <span>{acc.name}</span>
                        <span>{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 px-2 font-semibold border-t">
                      <span>Total Revenue</span>
                      <span>{formatCurrency(statements.incomeStatement.revenue.total)}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-600 mb-2">Expenses</h3>
                    {statements.incomeStatement.expenses.accounts.map((acc, i) => (
                      <div key={i} className="flex justify-between py-1 px-2">
                        <span>{acc.name}</span>
                        <span>{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 px-2 font-semibold border-t">
                      <span>Total Expenses</span>
                      <span>{formatCurrency(statements.incomeStatement.expenses.total)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between p-4 bg-blue-50 rounded-lg font-bold text-lg">
                    <span>{statements.incomeStatement.net_income_label}</span>
                    <span className={statements.incomeStatement.net_income >= 0 ? "text-green-600" : "text-red-600"}>
                      {formatCurrency(statements.incomeStatement.net_income)}
                    </span>
                  </div>
                </div>
              )}

              {selectedStatement === "balance" && statements.balanceSheet && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-blue-600 mb-2">Assets</h3>
                    <h4 className="text-sm font-medium mt-2">Current Assets</h4>
                    {statements.balanceSheet.assets.current_assets.map((acc, i) => (
                      <div key={i} className="flex justify-between py-1 px-2 text-sm">
                        <span>{acc.name}</span>
                        <span>{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                    <h4 className="text-sm font-medium mt-2">Fixed Assets</h4>
                    {statements.balanceSheet.assets.fixed_assets.map((acc, i) => (
                      <div key={i} className="flex justify-between py-1 px-2 text-sm">
                        <span>{acc.name}</span>
                        <span>{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 px-2 font-semibold border-t mt-2">
                      <span>Total Assets</span>
                      <span>{formatCurrency(statements.balanceSheet.assets.total)}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-yellow-600 mb-2">Liabilities</h3>
                    {statements.balanceSheet.liabilities.current_liabilities.map((acc, i) => (
                      <div key={i} className="flex justify-between py-1 px-2 text-sm">
                        <span>{acc.name}</span>
                        <span>{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 px-2 font-semibold border-t">
                      <span>Total Liabilities</span>
                      <span>{formatCurrency(statements.balanceSheet.liabilities.total)}</span>
                    </div>

                    <h3 className="font-semibold text-green-600 mb-2 mt-4">Equity</h3>
                    {statements.balanceSheet.equity.accounts.map((acc, i) => (
                      <div key={i} className="flex justify-between py-1 px-2 text-sm">
                        <span>{acc.name}</span>
                        <span>{formatCurrency(acc.balance)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-1 px-2 text-sm">
                      <span>Retained Earnings</span>
                      <span>{formatCurrency(statements.balanceSheet.equity.retained_earnings)}</span>
                    </div>
                    <div className="flex justify-between py-2 px-2 font-semibold border-t">
                      <span>Total Equity</span>
                      <span>{formatCurrency(statements.balanceSheet.equity.total)}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FinancialStatements;
